import { sprintReportingPath, clientsReportingPath, baseAppPath, organizationsPath } from '@app/constants/url';
import { UserEntity, withAuth } from '@data';
import { useOrganizations } from '@data/hooks/organizations';
import React, { ReactElement, useEffect } from 'react';
import { useStore } from '@data/storages';
import { Roles } from '@app/utils/types';
import { useOrganizationId } from '@app/utils/hooks';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Loader } from '@ui';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import SprintReporting from './sprintReporting';
import Members from './members';
import MembersReporting from './membersReporting';
import Products from './products';
import ProductsReporting from './productsReporting';
import ClientReporting from './clientReporting';
import Chapters from './chapters';
import Clients from './clients';
import Insights from './insights';
import Incidents from './incidents';
import Settings from './settings';

export interface OrganizationsProps {
  user: UserEntity;
}

const AuthorizedProductsReportingRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager],
  preloader: <Loader />,
})(ProductsReporting);

const AuthorizedMembersRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager],
  preloader: <Loader />,
})(Members);

const AuthorizedMembersReportingListRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager],
  preloader: <Loader />,
})(MembersReporting);

const AuthorizedProductsRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager],
  preloader: <Loader />,
})(Products);

const AuthorizedSprintReportingRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager, Roles.teamMember],
  preloader: <Loader />,
})(SprintReporting);

const AuthorizedClientsReportingRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.client],
  preloader: <Loader />,
})(ClientReporting);

const AuthorizedChaptersRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin],
  preloader: <Loader />,
})(Chapters);

// Admin or PM, matching the @Authorized on every read this page makes. No client ever reaches it.
const AuthorizedInsightsRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager],
  preloader: <Loader />,
})(Insights);

/* The queue is every incident on every product in the organization, so it is a manager's and an
   admin's. A client sees nothing about incidents at all. */
const AuthorizedIncidentsRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager],
  preloader: <Loader />,
})(Incidents);

// Admin or PM, matching the @Authorized on the three endpoints this screen drives.
const AuthorizedClientsRoute = withAuth({
  redirectTo: baseAppPath,
  allowedRoles: [Roles.admin, Roles.productManager],
  preloader: <Loader />,
})(Clients);

/** Fallback when no child route matches: never build paths with an empty org id (avoids `//` in URLs). */
const DefaultOrgRedirect = ({ user }: { user: UserEntity }): ReactElement => {
  const { organizationId: routeOrganizationId = '' } = useParams<{ organizationId: string }>();
  const { organization } = useStore();
  const { data: orgs, isPending } = useOrganizations(user.id);

  const effectiveOrgId = organization?.id ?? routeOrganizationId;
  if (!effectiveOrgId) {
    return <Navigate to={organizationsPath} replace />;
  }

  const roleResolved = organization?.role ?? orgs?.items.find(o => o.id === effectiveOrgId)?.role;
  if (roleResolved === undefined && isPending) {
    return <Loader />;
  }

  const to = roleResolved === Roles.client ? clientsReportingPath(effectiveOrgId) : sprintReportingPath(effectiveOrgId);
  return <Navigate to={to} replace />;
};

const Organization = ({ user }: OrganizationsProps): ReactElement => {
  const organizationId = useOrganizationId();
  const { t } = useTranslation();

  const { organization, setOrganization } = useStore();
  const { data: organizations } = useOrganizations(user?.id);

  useEffect(() => {
    if (!organizationId || !organizations?.items?.length) return;
    const next = organizations.items.find(o => o.id === organizationId);
    if (next) setOrganization(next);
  }, [organizationId, organizations?.items, setOrganization]);

  return (
    <>
      <Helmet>
        <title>
          {organization?.name ? `${organization.name} ${t('titles.sprintReporting')}` : t('titles.sprintReporting')}
        </title>
      </Helmet>
      <Routes>
        <Route path="products-reporting/*" element={<AuthorizedProductsReportingRoute />} />
        <Route path="client-reporting/*" element={<AuthorizedClientsReportingRoute />} />
        <Route path="sprint-reporting/*" element={<AuthorizedSprintReportingRoute user={user} />} />
        <Route path="members/*" element={<AuthorizedMembersRoute user={user} />} />
        <Route path="members-reporting/*" element={<AuthorizedMembersReportingListRoute />} />
        <Route path="products/*" element={<AuthorizedProductsRoute user={user} />} />
        <Route path="insights/*" element={<AuthorizedInsightsRoute />} />
        <Route path="incidents/*" element={<AuthorizedIncidentsRoute />} />
        <Route path="chapters/*" element={<AuthorizedChaptersRoute />} />
        <Route path="clients/*" element={<AuthorizedClientsRoute />} />
        {/* Everyone's own, so no role gate. */}
        <Route path="settings" element={<Settings user={user} />} />
        <Route path="*" element={<DefaultOrgRedirect user={user} />} />
      </Routes>
    </>
  );
};

export default Organization;
